import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { chargeDiffersFromList } from "@/components/booking/AppointmentTicket";
import { apiGet } from "@/lib/api-error";
import { formatCurrency } from "@/lib/format";

export type GroupPaymentMethod = "monri" | "bank_transfer";

interface GroupPriceQuote {
  groupTherapyId: string;
  priceCharged: number;
  chargeCountry: string | null;
}

/**
 * Iznos koji prijava stvarno naplaćuje (/api/group-therapies/:id/price-quote).
 * Prikazana cijena ciklusa prati regiju koju klijent sam bira, a naplata zemlju
 * konekcije, pa kad se razlikuju ide isto kratko objašnjenje kao kod seansi.
 */
export function GroupEnrollAmount({
  listPrice,
  chargeAmount,
}: {
  listPrice: number;
  chargeAmount: number;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid="gt-charge-amount" className="rounded-lg border border-[#ece8df] p-3">
      <p className="text-sm font-semibold text-foreground">
        {t("booking.amountToPay", { amount: formatCurrency(chargeAmount) })}
      </p>
      {chargeDiffersFromList(chargeAmount, listPrice) && (
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {t("booking.amountByConnection")}
        </p>
      )}
    </div>
  );
}

export function GroupEnrollDialog({
  open,
  onClose,
  cycleId,
  listPrice,
  paymentMethod,
  onPaymentMethodChange,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  cycleId: string;
  listPrice: number;
  paymentMethod: GroupPaymentMethod;
  onPaymentMethodChange: (method: GroupPaymentMethod) => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  const { data: quote, isLoading: quoteLoading } = useQuery({
    queryKey: ["group-therapy", "price-quote", cycleId],
    queryFn: () =>
      apiGet<GroupPriceQuote>(
        `/api/group-therapies/${encodeURIComponent(cycleId)}/price-quote`,
        "Greška pri izračunu iznosa",
      ),
    enabled: open,
    staleTime: 60_000,
  });

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <DialogTitle>Prijava na grupnu terapiju</DialogTitle>
        <DialogDescription>
          Cijena ciklusa: <strong>{formatCurrency(listPrice)}</strong>. Plaćanje je za cijeli
          ciklus odjednom. Otkazivanje je moguće samo prije početka prve sesije.
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        <div className="space-y-3">
          {quote && <GroupEnrollAmount listPrice={listPrice} chargeAmount={quote.priceCharged} />}
          <PaymentChoice
            testId="gt-pay-monri"
            selected={paymentMethod === "monri"}
            onClick={() => onPaymentMethodChange("monri")}
            title="Online (Monri)"
            description="Karticom odmah preko Monri sigurne stranice."
          />
          <PaymentChoice
            testId="gt-pay-bank_transfer"
            selected={paymentMethod === "bank_transfer"}
            onClick={() => onPaymentMethodChange("bank_transfer")}
            title="Uplata na račun"
            description="Dobit ćete uputstva za bankovnu uplatu."
          />
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Otkaži
        </Button>
        <Button
          data-testid="gt-enroll-confirm"
          onClick={onConfirm}
          disabled={isPending || quoteLoading}
        >
          {isPending ? <CircleNotch className="size-4 animate-spin" /> : null}
          Potvrdi prijavu
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function PaymentChoice({
  selected,
  onClick,
  title,
  description,
  testId,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  description: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`w-full rounded-lg border p-4 text-left transition ${
        selected ? "border-primary bg-primary/5" : "border-[#ece8df] hover:border-foreground/20"
      }`}
    >
      <p className="font-medium text-foreground">{title}</p>
      <p className="mt-1 text-xs text-muted-foreground">{description}</p>
    </button>
  );
}
